import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { api } from '../api';
import type { ResponseItem } from '../types';

type Props = { onClose: () => void; adminKey?: string };

export function Archive({ onClose, adminKey }: Props) {
  const [weeks, setWeeks] = useState<Array<{ weekKey: string; count: number }>>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [items, setItems] = useState<ResponseItem[]>([]);
  const [q, setQ] = useState('');

  useEffect(() => { api.responses.weeks(adminKey).then(setWeeks).catch(() => {}); }, [adminKey]);
  useEffect(() => {
    if (!selected) return;
    api.responses.listWeek(selected, adminKey).then(r => setItems(r.items)).catch(() => {});
  }, [selected, adminKey]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter(r =>
      [r.perfect, r.wrong, r.timeSink, r.ask, r.memberName]
        .some(x => (x || '').toLowerCase().includes(s))
    );
  }, [items, q]);

  return (
    <>
      <motion.div
        className="sheet-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.aside
        className="sheet"
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 32, stiffness: 320 }}
        role="dialog" aria-label="Weekly Archive"
      >
        <div className="sheet-handle" />
        <header className="px-4 py-2 border-b border-coast-hairline flex items-center justify-between">
          <h2 className="font-heading text-2xl">Archive</h2>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </header>

        {!selected ? (
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
            {weeks.length === 0 && <div className="text-sm text-coast-muted">No weeks yet.</div>}
            {weeks.map(w => (
              <button
                key={w.weekKey}
                onClick={() => setSelected(w.weekKey)}
                className="w-full flex items-center justify-between px-4 py-4
                           border border-coast-hairline bg-coast-card
                           active:border-coast-blue"
              >
                <div className="text-left">
                  <div className="text-base font-heading">{w.weekKey}</div>
                  <div className="text-[10px] uppercase tracking-widest text-coast-muted">
                    {w.count} response{w.count === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="text-coast-blue text-xl leading-none">›</div>
              </button>
            ))}
          </div>
        ) : (
          <>
            <div className="px-4 py-2 border-b border-coast-hairline flex items-center gap-2">
              <button className="btn btn-ghost text-xs" onClick={() => setSelected(null)}>← Weeks</button>
              <input
                className="input flex-1"
                placeholder="Search this week…"
                value={q}
                onChange={e => setQ(e.target.value)}
              />
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {filtered.map(r => (
                <div key={r.id} className="card">
                  <div className="text-xs uppercase tracking-widest text-coast-blue mb-2">{r.memberName}</div>
                  <Row label="Perfect" value={r.perfect} color="text-semantic-green" />
                  <Row label="Wrong" value={r.wrong} color="text-semantic-red" />
                  <Row label="Time Sink" value={r.timeSink} color="text-semantic-amber" />
                  <Row label="Ask" value={r.ask} color="text-coast-blue" />
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="text-coast-muted text-sm text-center py-6">No responses match.</div>
              )}
            </div>
          </>
        )}
      </motion.aside>
    </>
  );
}

function Row({ label, value, color }: { label: string; value: string; color: string }) {
  if (!value?.trim()) return null;
  return (
    <div className="py-1">
      <div className={`text-[10px] uppercase tracking-widest ${color}`}>{label}</div>
      <div className="text-[15px] whitespace-pre-wrap">{value}</div>
    </div>
  );
}
